import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';
import Icons from '../../../../../components/Icons/Icons';
import InfoPopup from '../../../../../components/InfoPopup/InfoPopup';
import { CUSTOM } from '../../../../../util/constants';
import { ShoutoutOption, TALENT_SERVICES_GUIDES } from '../../../../../util/enums';
import css from './SignatureServices.module.css';
import GuidePopup from '../../../../../components/GuidePopup/GuidePopup';

const shoutoutOptions = [
  'Birthday Shoutout',
  'Anniversary Shoutout',
  'Congratulatory Message',
  'Appreciation Shoutout',
  'Holiday Shoutout',
  'Engagement Announcement',
  'Retirement Congratulations',
  'Motivational Message',
  'Good Luck Message',
  'Wedding Shoutout',
  'Custom',
];

const shoutoutTooltips = {
  'Birthday Shoutout': 'Provide a personalized birthday wish to make a special day even brighter!',
  'Anniversary Shoutout':
    'Record heartfelt anniversary messages to help fans celebrate their special milestones.',
  'Congratulatory Message': "Create personalized congratulations to recognize fans' achievements.",
  'Appreciation Shoutout': 'Craft custom thank-you messages to help fans show their gratitude.',
  'Holiday Shoutout': 'Spread festive cheer with special holiday greetings for your fans.',
  'Engagement Announcement': "Create heartfelt messages to make fans' engagements extra special.",
  'Retirement Congratulations': "Honor someone's career with your personalized retirement message.",
  'Motivational Message': 'Provide inspiring messages to encourage fans, athletes, or students.',
  'Good Luck Message': 'Send custom good luck shoutouts before big events or games.',
  'Wedding Shoutout': 'Surprise couples with your personalized wedding greeting.',
  Custom: 'Create a custom shoutout offering in your own unique style.',
};

const Shoutouts = ({ values, viewport }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const isMobileLayout = viewport.width && viewport.width > 0 && viewport.width < 768;

  const handleAccordionClick = (index, e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL') {
      e.stopPropagation();
      return;
    }
    setActiveIndex(index === activeIndex ? null : index);
  };

  const faqsData = [
    {
      question: 'Shoutouts or Greetings',
      answer: (
        <FieldArray name="shoutouts">
          {({ fields }) => {
            const fieldValues = values.shoutouts || [];

            return (
              <div className={css.shoutoutSec} onClick={e => e.stopPropagation()}>
                {shoutoutOptions.map((option, idx) => {
                  const currentShoutoutIndex = fieldValues.findIndex(
                    item => item.option === option
                  );
                  const currentShoutout =
                    currentShoutoutIndex !== -1 ? fieldValues[currentShoutoutIndex] : {};

                  return (
                    <div className={css.formFld} key={option}>
                      <label className={css.talentOptions} onClick={e => e.stopPropagation()}>
                        <Field
                          name={`shoutouts[${
                            currentShoutoutIndex !== -1 ? currentShoutoutIndex : fields.length
                          }].selected`}
                          component="input"
                          type="checkbox"
                          checked={!!currentShoutout.selected}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const isChecked = e.target.checked;

                            if (isChecked) {
                              if (currentShoutoutIndex === -1) {
                                fields.push({
                                  option,
                                  selected: true,
                                  ...(option === CUSTOM ? { customItems: [] } : {}),
                                });
                              }
                            } else {
                              if (currentShoutoutIndex !== -1) {
                                fields.remove(currentShoutoutIndex);
                              }
                            }
                          }}
                        />
                        <span>
                          {option}
                          {shoutoutTooltips[option] && (
                            <InfoPopup triggerOn="hover" info={shoutoutTooltips[option]} />
                          )}
                        </span>
                      </label>

                      {currentShoutout.selected && option !== CUSTOM && (
                        <div
                          className={classNames(
                            css.customFldSec,
                            option === ShoutoutOption.HOLIDAY_SHOUTOUT && css.holidayCustomSec
                          )}
                        >
                          <span className={css.currency}>$</span>
                          <Field
                            className={css.input}
                            name={`shoutouts[${currentShoutoutIndex}].price`}
                            component="input"
                            type="number"
                            placeholder={`Enter flat price for ${option}`}
                            onClick={e => e.stopPropagation()}
                          />
                          <span className={css.price}>.00</span>
                        </div>
                      )}

                      {currentShoutout.selected && option === CUSTOM && (
                        <FieldArray name={`shoutouts[${currentShoutoutIndex}].customItems`}>
                          {({ fields: customFields }) => (
                            <div className={css.customInputs}>
                              {customFields.map((name, index) => (
                                <div key={name} className={css.customItemRow}>
                                  <Field
                                    name={`${name}.title`}
                                    component="input"
                                    type="text"
                                    placeholder="Enter custom title"
                                    className={css.input}
                                    onClick={e => e.stopPropagation()}
                                  />
                                  <div className={css.customFldSec}>
                                    <span className={css.currency}>$</span>
                                    <Field
                                      name={`${name}.price`}
                                      component="input"
                                      type="number"
                                      placeholder="Enter flat price for Custom shoutout"
                                      className={css.input}
                                      onClick={e => e.stopPropagation()}
                                    />
                                    <span className={css.price}>.00</span>
                                  </div>
                                  <button
                                    type="button"
                                    className={css.removeBtn}
                                    onClick={e => {
                                      e.stopPropagation();
                                      customFields.remove(index);
                                    }}
                                  >
                                    {isMobileLayout ? (
                                      <>
                                        <Icons name="bin" /> Delete
                                      </>
                                    ) : (
                                      <Icons name="bin" />
                                    )}
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className={css.addBtn}
                                onClick={e => {
                                  e.stopPropagation();
                                  customFields.push({ title: '', price: '' });
                                }}
                              >
                                + &nbsp; Add Custom Item
                              </button>
                            </div>
                          )}
                        </FieldArray>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }}
        </FieldArray>
      ),
    },
  ];

  return (
    <>
      {faqsData.map((faq, index) => (
        <div
          key={index}
          className={classNames(css.faqCard, {
            [css.activeFAQ]: activeIndex === index,
          })}
          onClick={e => handleAccordionClick(index, e)}
        >
          <div className={css.faqBody}>
            <span className={css.faqHeading}>
              <div>
                {faq?.question}{' '}
                <GuidePopup externalLink={TALENT_SERVICES_GUIDES.SHOUTOUTS_OR_GREETINGS} />
              </div>
              <span className={css.faqIcon}>
                {activeIndex === index ? <Icons name="upAngle" /> : <Icons name="downAngle" />}
              </span>
            </span>
            {activeIndex === index && <div className={css.faqDescription}>{faq?.answer}</div>}
          </div>
        </div>
      ))}
    </>
  );
};

export default Shoutouts;
